import { isValid } from 'date-fns';

export interface IAppointmentInterval {
  id?: string;
  vehicle_id?: string;
  start_date: string | Date;
  end_date: string | Date;
  status?: string;
}

export interface IAppointmentSlot {
  date: string;
  available: boolean;
}

const NON_BLOCKING_STATUSES = [
  'recusado',
  'rejeitado',
  'cancelado',
  'cancelled',
];

function normalizeStatus(status?: string): string {
  return (status || '').trim().toLocaleLowerCase('pt-BR');
}

export function isBlockingAppointment(
  appointment: IAppointmentInterval,
): boolean {
  return !NON_BLOCKING_STATUSES.includes(normalizeStatus(appointment.status));
}

export function filterAppointmentsByVehicle<T extends IAppointmentInterval>(
  appointments: T[],
  vehicleId: string,
): T[] {
  return appointments.filter(
    appointment => appointment.vehicle_id === vehicleId,
  );
}

function toValidDate(value: string | Date): Date | null {
  const date =
    value instanceof Date ? new Date(value.getTime()) : new Date(value);

  return isValid(date) ? date : null;
}

export function appointmentOverlapsRange(
  appointment: IAppointmentInterval,
  rangeStart: string | Date,
  rangeEnd: string | Date,
): boolean {
  if (!isBlockingAppointment(appointment)) return false;

  const appointmentStart = toValidDate(appointment.start_date);
  const appointmentEnd = toValidDate(appointment.end_date);
  const start = toValidDate(rangeStart);
  const end = toValidDate(rangeEnd);

  if (!appointmentStart || !appointmentEnd || !start || !end) return false;

  // Intervalos semiabertos: [início, fim). Um veículo que retorna às 19h
  // pode ser reservado novamente a partir das 19h.
  return appointmentStart < end && appointmentEnd > start;
}

export function getAppointmentsForDay<T extends IAppointmentInterval>(
  appointments: T[],
  day: Date,
): T[] {
  const dayStart = new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    0,
    0,
    0,
    0,
  );
  const nextDay = new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate() + 1,
    0,
    0,
    0,
    0,
  );

  return appointments.filter(appointment =>
    appointmentOverlapsRange(appointment, dayStart, nextDay),
  );
}

export function createHalfHourAppointmentSlots(
  appointments: IAppointmentInterval[],
  day: Date,
  now: Date = new Date(),
): IAppointmentSlot[] {
  const slots: IAppointmentSlot[] = [];

  for (let index = 0; index < 48; index += 1) {
    const slot = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      0,
      index * 30,
      0,
      0,
    );

    if (slot >= now) {
      const available = !appointments.some(appointment => {
        if (!isBlockingAppointment(appointment)) return false;

        const appointmentStart = toValidDate(appointment.start_date);
        const appointmentEnd = toValidDate(appointment.end_date);

        if (!appointmentStart || !appointmentEnd) return false;

        return slot >= appointmentStart && slot < appointmentEnd;
      });

      slots.push({
        date: slot.toISOString(),
        available,
      });
    }
  }

  return slots;
}

export function hasAvailableSlotInDay(
  appointments: IAppointmentInterval[],
  day: Date,
  now: Date = new Date(),
): boolean {
  return createHalfHourAppointmentSlots(appointments, day, now).some(
    slot => slot.available,
  );
}

export function isAppointmentRangeAvailable(
  appointments: IAppointmentInterval[],
  rangeStart: string | Date,
  rangeEnd: string | Date,
): boolean {
  const start = toValidDate(rangeStart);
  const end = toValidDate(rangeEnd);

  if (!start || !end || end <= start) return false;

  return !appointments.some(appointment =>
    appointmentOverlapsRange(appointment, start, end),
  );
}
